import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import Pagination from '@mui/material/Pagination';
import { Skeleton } from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import {
  EntityDocument,
  EntityDocumentSource,
} from 'api/bitedb/entities/entityDocuments.service';
import { IconArrowRight, IconDatabase } from 'pages/import/tools/ToolsIcons';
import {
  IconDownload,
  IconFileText,
  IconSearch,
  IconTable,
} from 'pages/import/library/LibraryIcons';
import 'pages/import/tools/importTools.css';
import 'pages/import/library/library.css';
import { EntityLibrary, LIBRARY_EXTENSIONS } from './useEntityLibrary';

type FilterKey = 'all' | EntityDocumentSource;

const LIBRARY_PAGE_SIZE = 25;

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'upload', label: 'Uploaded' },
  { key: 'beneficial_ownership', label: 'Beneficial Ownership' },
  { key: 'entity_risk', label: 'Export and Transshipment Risk' },
];

const SOURCE_LABELS: Record<EntityDocumentSource, string> = {
  upload: 'Uploaded',
  beneficial_ownership: 'Beneficial Ownership investigation',
  entity_risk: 'Export and Transshipment Risk run',
};

const formatDate = (iso: string) => {
  const date = moment(iso);
  return date.isValid() ? date.format('MM/DD/YYYY') : '—';
};

const formatBytes = (size: number | null): string => {
  if (size === null || !Number.isFinite(size)) return '—';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const isPdf = (document: EntityDocument) =>
  document.content_type === 'application/pdf' ||
  /\.pdf$/i.test(document.file_name);

/**
 * A file can be removed by the member who filed it, or by a team
 * administrator for anyone's file.
 */
export const canDeleteEntityDocument = (
  document: EntityDocument,
  currentUserId?: string,
  canManageDocuments = false,
): boolean =>
  canManageDocuments ||
  Boolean(currentUserId && document.uploaded_by === currentUserId);

interface EntityHubLibraryProps {
  entityName: string;
  library: EntityLibrary;
  currentUserId?: string;
  canManageDocuments?: boolean;
  onBack: () => void;
}

/**
 * The record's Library as a Deep Dive tool page, on the Import Hub Library's
 * own table, chips and search, with upload and delete for the record's files.
 */
const EntityHubLibrary: React.FC<EntityHubLibraryProps> = ({
  entityName,
  library,
  currentUserId,
  canManageDocuments = false,
  onBack,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [filter, setFilter] = useState<FilterKey>('all');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [dragging, setDragging] = useState(false);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const { documents, loading, failed, uploads, deletingId } = library;

  const matching = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return documents.filter(
      (document) =>
        (filter === 'all' || (document.source || 'upload') === filter) &&
        (!needle ||
          document.file_name.toLowerCase().includes(needle) ||
          (document.uploaded_by_name || '').toLowerCase().includes(needle)),
    );
  }, [documents, filter, query]);

  const pageCount = Math.max(1, Math.ceil(matching.length / LIBRARY_PAGE_SIZE));
  useEffect(() => {
    setPage(1);
  }, [filter, query]);
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);
  const rows = matching.slice(
    (page - 1) * LIBRARY_PAGE_SIZE,
    page * LIBRARY_PAGE_SIZE,
  );

  const chooseFiles = () => fileInputRef.current?.click();
  const dropFiles = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    library.upload(Array.from(event.dataTransfer.files || []));
  };

  const count = documents.length;

  return (
    <div
      className={`iml-root iml-style-island iml-page is-library entity-hub-library${
        dragging ? ' is-dragging' : ''
      }`}
      onDragEnter={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
          setDragging(false);
        }
      }}
      onDrop={dropFiles}
    >
      <div className='iml-page-bar entity-hub-risk-head'>
        <button type='button' className='iml-back' onClick={onBack}>
          <IconArrowRight className='iml-back-icon' />
          Back to tools
        </button>
        <div className='entity-hub-risk-title'>
          <h2 className='iml-page-title'>
            <span className='iml-row-icon' aria-hidden='true'>
              <IconDatabase className='iml-icon' />
            </span>
            Library
            {count > 0 ? (
              <span className='entity-hub-tool-pill'>
                {count.toLocaleString()} {count === 1 ? 'file' : 'files'}
              </span>
            ) : null}
          </h2>
          <p>
            Files kept against {entityName || 'this record'}: uploaded by your
            team, and the report from each Beneficial Ownership investigation
            and Export and Transshipment Risk run once it finishes.
          </p>
        </div>
        <button
          type='button'
          className='entity-hub-btn primary'
          onClick={chooseFiles}
        >
          <UploadFileOutlinedIcon />
          Upload files
        </button>
        <input
          ref={fileInputRef}
          type='file'
          multiple
          hidden
          accept={LIBRARY_EXTENSIONS.map((extension) => `.${extension}`).join(
            ',',
          )}
          onChange={(event) => {
            const files = Array.from(event.target.files || []);
            event.target.value = '';
            library.upload(files);
          }}
          aria-label='Choose files to upload'
        />
      </div>

      <div className='dlb-root'>
        <div
          className={`entity-hub-library-dropzone${
            dragging ? ' is-dragging' : ''
          }`}
          role='button'
          tabIndex={0}
          onClick={chooseFiles}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              chooseFiles();
            }
          }}
        >
          <UploadFileOutlinedIcon />
          <span>
            <strong>Drop files anywhere on this page</strong> or choose files ·
            PDF, Office, CSV, text or images, up to 25 MB each
          </span>
        </div>

        {uploads.map((item) => (
          <div className='entity-hub-library-upload' key={item.key}>
            <div>
              <strong>{item.fileName}</strong>
              <span className='entity-hub-library-num'>{item.progress}%</span>
            </div>
            <div className='entity-hub-library-progress'>
              <span style={{ width: `${item.progress}%` }} />
            </div>
          </div>
        ))}

        <div className='dlb-toolbar'>
          <div className='dlb-chips'>
            {FILTERS.map((item) => (
              <button
                key={item.key}
                type='button'
                className={`dlb-chip${filter === item.key ? ' is-active' : ''}`}
                onClick={() => setFilter(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <div className='dlb-search'>
            <IconSearch />
            <input
              className='dlb-search-input'
              placeholder='Search by file name or person'
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              aria-label='Search the Library'
            />
          </div>
        </div>

        <div className='dlb-table-wrap'>
          <table className='dlb-table entity-hub-library-table'>
            <thead>
              <tr>
                <th>File</th>
                <th>Source</th>
                <th>Added by</th>
                <th>Date</th>
                <th>Size</th>
                <th aria-label='Actions' />
              </tr>
            </thead>
            <tbody>
              {loading && count === 0
                ? [0, 1, 2].map((row) => (
                    <tr key={`skeleton-${row}`} aria-hidden='true'>
                      {[0, 1, 2, 3, 4, 5].map((cell) => (
                        <td key={cell}>
                          <Skeleton variant='text' />
                        </td>
                      ))}
                    </tr>
                  ))
                : rows.map((document) => {
                    const source = document.source || 'upload';
                    const canRemove = canDeleteEntityDocument(
                      document,
                      currentUserId,
                      canManageDocuments,
                    );
                    return (
                      <tr key={document.id}>
                        <td>
                          <div className='dlb-source'>
                            <div
                              className={`dlb-source-icon${
                                isPdf(document) ? ' is-pdf' : ''
                              }`}
                            >
                              <IconFileText />
                            </div>
                            <button
                              type='button'
                              className='dlb-source-download'
                              onClick={() => library.download(document)}
                              title={`Download ${document.file_name}`}
                            >
                              <span>{document.file_name}</span>
                              <IconDownload />
                            </button>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`dlb-badge${
                              source !== 'upload'
                                ? ' is-dataset'
                                : ''
                            }`}
                          >
                            {SOURCE_LABELS[source]}
                          </span>
                        </td>
                        <td className='dlb-muted'>
                          {document.uploaded_by_name || 'Unknown user'}
                        </td>
                        <td className='dlb-muted entity-hub-library-num'>
                          {formatDate(document.created_at)}
                        </td>
                        <td className='dlb-muted entity-hub-library-num'>
                          {formatBytes(document.file_size)}
                        </td>
                        <td className='entity-hub-library-actions-cell'>
                          <div className='entity-hub-library-actions'>
                            {canRemove && confirmingId === document.id ? (
                              <>
                                <button
                                  type='button'
                                  className='entity-hub-btn compact'
                                  onClick={() => setConfirmingId(null)}
                                  disabled={deletingId === document.id}
                                >
                                  Cancel
                                </button>
                                <button
                                  type='button'
                                  className='entity-hub-btn compact is-danger'
                                  onClick={async () => {
                                    if (await library.remove(document.id)) {
                                      setConfirmingId(null);
                                    }
                                  }}
                                  disabled={deletingId === document.id}
                                >
                                  {deletingId === document.id
                                    ? 'Removing…'
                                    : 'Remove'}
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type='button'
                                  className='entity-hub-library-icon-btn'
                                  aria-label={`Download ${document.file_name}`}
                                  onClick={() => library.download(document)}
                                >
                                  <IconDownload />
                                </button>
                                {canRemove ? (
                                  <button
                                    type='button'
                                    className='entity-hub-library-icon-btn'
                                    aria-label={`Remove ${document.file_name}`}
                                    onClick={() => setConfirmingId(document.id)}
                                  >
                                    <DeleteOutlineOutlinedIcon />
                                  </button>
                                ) : null}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>

          {!loading && failed && count === 0 ? (
            <div className='dlb-empty'>
              <div className='dlb-empty-text'>
                The Library could not be loaded.
              </div>
              <button
                type='button'
                className='entity-hub-btn compact entity-hub-library-retry'
                onClick={library.reload}
              >
                Try again
              </button>
            </div>
          ) : !loading && rows.length === 0 ? (
            <div className='dlb-empty'>
              <div className='dlb-empty-icon'>
                <IconTable />
              </div>
              <div className='dlb-empty-title'>
                {count === 0 ? 'No files yet' : 'No matching files'}
              </div>
              <div className='dlb-empty-text'>
                {count === 0
                  ? 'Upload a file, or run a Beneficial Ownership investigation to file its report here.'
                  : 'Try a different search term or filter.'}
              </div>
            </div>
          ) : null}

          {matching.length > LIBRARY_PAGE_SIZE ? (
            <div className='hb-pagination dlb-pagination'>
              <span className='dlb-pagination-summary'>
                Page {page} of {pageCount} · {matching.length} files
              </span>
              <Pagination
                page={page}
                count={pageCount}
                size='small'
                shape='rounded'
                onChange={(_event, nextPage) => setPage(nextPage)}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default EntityHubLibrary;
